import { PageModule, Survey, SurveyQuestion, SurveyQuestionType } from "../../../../../types/working-model";
import TextSurveyQuestion from './text-survey-question';
import RadioSurveyQuestion from './radio-survey-question';
import CheckboxSurveyQuestion from './checkbox-survey-question';
import { useState, useCallback, useMemo, useEffect } from "react";
import { TemplateClassNames } from "../../../../../types/template-layouts";
import { useDispatch } from "react-redux";
import { resetSurvey } from "../../../../../store/actions/event/event-actions";
import { useTranslation } from "react-i18next";

interface QuestionProps {
	survey: Survey;
	module: PageModule;
	template: TemplateClassNames;
	preview?: boolean;
	sessionUuid: string;
}

const Question: React.FC<QuestionProps> = ({ survey, module, template, preview, sessionUuid }) => {
	const [currentQuestion, setCurrentQuestion] = useState(0);
	const [viewAll, setViewAll] = useState(false);
	const [resetting, setResetting] = useState(false);
	const maxQuestions = survey.questions.length;

	const dispatch = useDispatch();
	const { t } = useTranslation("session");

	const renderSurveyQuestion = (question: SurveyQuestion) => {
		switch (question.question_type) {
			case SurveyQuestionType.text:
				return <TextSurveyQuestion sessionUuid={sessionUuid} key={question.survey_question} question={question} module={module} template={template} />;
			case SurveyQuestionType.radio:
				return <RadioSurveyQuestion key={question.survey_question} question={question} module={module} template={template} showResults={survey.show_results} />;
			case SurveyQuestionType.checkbox:
				return <CheckboxSurveyQuestion sessionUuid={sessionUuid} key={question.survey_question} question={question} module={module} template={template} showResults={survey.show_results} />;
			default: return null;
		}
	};

	const reset = useCallback(async () => {
		if (!preview) {
			setResetting(true);
			const questions = survey.questions.map((question: SurveyQuestion) => {
				return question.survey_question;
			});
			// including await to make sure the survey renders with the cleared redux store data
			await dispatch(resetSurvey(questions));
			setResetting(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (survey.questions.length <= 1) {
			setViewAll(true);
		}
	}, [survey]);

	const pagination = useMemo(() => {
		return (
			<div className='survey-pagination'>
				<button disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(currentQuestion - 1)}>{t("Previous")}</button>
				<span>{currentQuestion + 1}/{maxQuestions}</span>
				<button disabled={currentQuestion + 1 === maxQuestions} onClick={() => setCurrentQuestion(currentQuestion + 1)}>{t("Next")}</button>
			</div>
		);
	}, [currentQuestion, maxQuestions, t]);

	return (
		<div className="question-list">
			<div className="view-all">
				{survey.questions.length > 1 ? (
					<button className="no-style clear" onClick={() => setViewAll(!viewAll)}>{viewAll ? t("Collapse Questions") : t("View All Questions")}</button>
				) : (<></>)}
				{survey.allow_retake && (
					<button className="no-style clear retake-button" onClick={reset}>{t("Retake Survey")}</button>
				)}
			</div>
			{resetting ? (<></>)
				: (viewAll ? (
					survey.questions.map((question: SurveyQuestion) => {
						return (
							<div className={"question-wrapper"} style={{ textIndent: '1em' }} key={question.survey_question}>
								{renderSurveyQuestion(question)}
							</div>
						);
					})
				) : (
					<>
						<div style={{ textIndent: '1em' }} key={survey.questions[currentQuestion].survey_question}>
							{renderSurveyQuestion(survey.questions[currentQuestion])}
						</div>
						{pagination}
					</>
				)
				)}
		</div>
	);
};

export default Question;